import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import Button from 'components/UI/Button';
import { ReactComponent as PaycashLogo } from 'assets/logos/paycash.svg';
import { ReactComponent as PuntoredLogo } from 'assets/logos/logo-puntored.svg';

import { formatDate } from 'utils/formatDate';
import { formatCurrency } from 'utils/formatCurrency';
import {
  createMultipleReference as createMultiplePaycashReference,
  createReference as createPaycashReference,
  deleteReference as deletePaycashReference,
} from 'modules/payCash/actions';
import {
  createMultipleReference as createMultiplePuntoredReference,
  createReference as createPuntoredReference,
  deleteReference as deletePuntoredReference,
} from 'modules/puntored/actions';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import Modal from 'components/Modal';
import { ROUTE_PATH } from 'routes';
import { getContracts } from 'modules/contracts/actions';
import { getSections } from 'modules/sections/actions';
import {
  CONTRACT,
  CONTRACT_STATUS,
  SECTION,
  PROVIDER_TYPE,
} from 'constants/global';
import { translate } from 'utils/translate';
import useConfiguration from 'hooks/useConfiguration';
import Translate from 'components/Translate';

import Barcode from 'react-barcode';
import './styles.scss';

const PaymentReference = ({
  id = '',
  type = '',
  payment = null,
  orders = [],
  isMultiple = false,
  amount = 0,
  coverageId = null,
  coverage = 0,
  provider = '',
  onClose = () => {},
  onDelete = () => {},
  onDownloadSectionNote = () => {},
}) => {
  const literals = useLiterals('contractPay');
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [reference, setReference] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDeleteReferenceModal, setShowDeleteReferenceModal] = useState(false);
  const { getConfigurationValue } = useConfiguration();
  const paycashReminderText = getConfigurationValue('paycashReminderText');
  const partnersUrls = useMemo(() => {
    const raw = getConfigurationValue('partners');
    if (!raw || typeof raw !== 'string') return [];
    return raw.trim().split(/\s+/);
  }, [getConfigurationValue]);
  const puntoredPartnersUrls = useMemo(() => {
    const raw = getConfigurationValue('partnersPuntored');
    if (!raw || typeof raw !== 'string') return [];
    return raw.trim().split(/\s+/);
  }, [getConfigurationValue]);
  const dispatch = useDispatch();
  const isPuntored = provider === PROVIDER_TYPE.PUNTORED;

  const handleOpenPaycashSearcher = () => {
    window.open('https://www.paycashglobal.com/buscador.php', '_blank', 'noopener,noreferrer');
  };

  const handleCreateReference = () => {
    const onSuccess = (response) => {
      setLoading(false);
      if (response) {
        setReference(response);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    };
    const onError = () => {
      setLoading(false);
      setShowErrorModal(true);
    };

    setLoading(true);
    if (isMultiple) {
      const createMultipleReference = isPuntored
        ? createMultiplePuntoredReference
        : createMultiplePaycashReference;

      createMultipleReference(amount, orders, provider)
        .then(onSuccess)
        .catch(onError);
    } else {
      const createReference = isPuntored
        ? createPuntoredReference
        : createPaycashReference;

      createReference(
        payment?.paymentKey,
        payment?.amount,
        amount || payment?.amount,
        type,
        id,
        payment?.title || null,
        coverage || 0,
        coverageId || null,
      ).then(onSuccess)
        .catch(onError);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    dispatch(type === SECTION ? getSections() : getContracts());
    if (isMultiple) {
      navigate(ROUTE_PATH.PAYMENTS);
    } else if (type === CONTRACT) {
      navigate(ROUTE_PATH.setContract(id));
    } else {
      navigate(ROUTE_PATH.setSection(id));
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    onClose();
  };

  const handleDeleteReference = () => {
    setLoading(true);
    const deleteReference = isPuntored
      ? deletePuntoredReference
      : deletePaycashReference;

    deleteReference(payment?.reference, provider)
      .then(() => {
        setLoading(false);
        setShowDeleteReferenceModal(false);
        dispatch(type === SECTION ? getSections() : getContracts());
        onDelete();
        onClose();
      })
      .catch(() => {
        setLoading(false);
        setShowErrorModal(true);
      });
  };

  return (
    <div className='payment-reference-resume'>
      {showDeleteReferenceModal && (
        <Modal
          title={literals.reference.deleteReference}
          onClose={() => setShowDeleteReferenceModal(false)}
        >
          <div className='text-center p-4'>
            <div className='text-neutral-500 mb-6'>{literals.reference.deleteReferenceDescription}</div>
            <div className='flex gap-2 justify-between'>
              <Button
                onClick={() => setShowDeleteReferenceModal(false)}
                type='secondary'
                text={literals.common.cancel}
              />
              <Button
                onClick={handleDeleteReference}
                type='primary'
                text={literals.common.accept}
                loading={loading}
              />
            </div>
          </div>
        </Modal>
      )}
      {showSuccessModal && (
        <SuccessModal
          title={
            isMultiple
              ? literals.reference.successMultipleTitle
              : translate(literals.reference[type === SECTION ? 'successSectionTitle' : 'successContractTitle'], { id })
          }
          onCloseNavigateRoute={ROUTE_PATH.HOME}
          onClose={handleCloseSuccessModal}
        >
          <div className='text-center p-4'>
            <div className='text-neutral-500 mb-4'>{literals.reference.successDescription}</div>
            <div className='text-primary font-bold mb-1'>{literals.reference.reference}</div>
            {((isMultiple ? reference[0] : reference)?.reference) ? (
              <div className='payment-reference-barcode mb-2'>
                <Barcode
                  className='w-full h-auto'
                  height={80}
                  value={String(
                    (isMultiple ? reference[0] : reference).reference,
                  )}
                />
              </div>
            ) : null}
            <div className='text-neutral-500 mb-2'>{(isMultiple ? reference[0] : reference)?.reference}</div>
            {isMultiple ? (
              <>
                <div className='text-primary font-bold mb-1'>{literals.reference.numberOfContracts}</div>
                <div className='text-neutral-500 mb-2'>{reference.length}</div>
              </>
            ) : (
              <>
                <div className='text-primary font-bold mb-1'>{literals.reference[type === CONTRACT ? 'contractNumber' : 'sectionNumber']}</div>
                <div className='text-neutral-500 mb-2'>{id}</div>
              </>
            )}
            <div className='text-primary font-bold mb-1'>{literals.reference.issueDate}</div>
            <div className='text-neutral-500 mb-2'>{formatDate((isMultiple ? reference[0] : reference)?.createdAt, 'DD/MM/YYYY')}</div>
            <div className='text-primary font-bold mb-1'>{literals.reference.amount}</div>
            <div className='text-neutral-500 mb-2'>{formatCurrency(isMultiple ? amount : (amount || payment?.amount || 0) + (coverage || 0))}</div>
            {!isPuntored && (
              <Button
                className='mt-4'
                type='primary'
                text={literals.reference.paycashSearchStoreButton}
                onClick={handleOpenPaycashSearcher}
              />
            )}
          </div>
        </SuccessModal>
      )}
      {showErrorModal && (
        <ErrorModal
          title={literals.reference.failureTitle}
          description={literals.reference.failureDescription}
          onClose={handleCloseErrorModal}
        />
      )}
      <h1 className='mb-6 text-primary'>{literals.paymetResume}</h1>
      {isMultiple ? (
        <div>
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{literals.contracts}</h2>
            <h3 className='text-neutral-500'>{orders.length}</h3>
          </div>
        </div>
      ) : (
        <>
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{type === CONTRACT ? literals.contract : literals.section}</h2>
            <h3 className='text-neutral-500'>{id}</h3>
          </div>
          {payment?.paymentName ? (
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{literals.paymentConcept}</h2>
              <h3 className='text-neutral-500 text-right'>{payment.paymentName}</h3>
            </div>
          ) : null}
          {payment?.reference ? (
            <>
              <div className='payment-reference-barcode mb-2'>
                <Barcode
                  className='w-full h-auto'
                  height={80}
                  value={String(payment.reference)}
                />
              </div>
              <div className='flex justify-between mb-2 gap-6 reference-container'>
                <h2 className='font-bold'>{literals.reference.reference}</h2>
                <h3 className='text-neutral-500'>{payment.reference}</h3>
              </div>
            </>
          ) : null}
          {payment?.limitDate && (
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{literals.date}</h2>
              <h3 className='text-neutral-500'>{payment?.limitDate}</h3>
            </div>
          )}
          {payment?.expirationDate && (
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{literals.date}</h2>
              <h3 className='text-neutral-500'>{formatDate(payment?.expirationDate, 'DD/MM/YYYY')}</h3>
            </div>
          )}
        </>
      )}
      {payment?.reference && payment?.status ? (
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.status}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.statusValues[payment?.status] || payment?.status}</h3>
        </div>
      ) : null}
      {payment?.authorizationNumber ? (
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.paymetAuthorization}</h2>
          <h3 className='text-neutral-500 font-bold'>{payment?.authorizationNumber}</h3>
        </div>
      ) : null}
      <div className='flex justify-between mb-2'>
        <h2 className='font-bold'>{literals.paymentMethod2}</h2>
        {isPuntored ? (
          <PuntoredLogo width={120} height={28} />
        ) : (
          <PaycashLogo width={120} height={28} />
        )}
      </div>
      <div className='separator my-4' />
      {coverage > 0 && (
        <>
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{type === CONTRACT ? literals.contractAmount : literals.sectionAmount}</h2>
            <h3 className='text-primary font-bold'>{formatCurrency(amount || payment?.amount || 0)}</h3>
          </div>
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{literals.coverageAmount}</h2>
            <h3 className='text-primary font-bold'>{formatCurrency(coverage)}</h3>
          </div>
        </>
      )}
      <div className='flex justify-between mb-2'>
        <h2 className='font-bold'>{literals.totalAmount}</h2>
        <h3 className='text-primary font-bold'>{formatCurrency((amount || payment?.amount || 0) + coverage)}</h3>
      </div>
      {!payment?.reference ? (
        <Button
          onClick={handleCreateReference}
          className='mt-6'
          type='primary'
          loading={loading}
          text={literals.reference.generatePaymentOrder}
        />
      ) : null}
      {(type === SECTION && payment?.reference && payment?.status === CONTRACT_STATUS.COMPLETED) ? (
        <Button
          onClick={onDownloadSectionNote}
          className='mt-6'
          type='primary'
          text={literals.reference.downloadNote}
        />
      ) : null}
      {(payment?.reference && payment?.status === CONTRACT_STATUS.PENDING) ? (
        <Button
          onClick={() => setShowDeleteReferenceModal(true)}
          className='mt-6'
          type='primary'
          text={literals.reference.deleteReference}
          loading={loading}
        />
      ) : null}
      {payment?.status !== CONTRACT_STATUS.COMPLETED && paycashReminderText && (
        <div className='text-left mt-6'>
          <Translate literal={paycashReminderText} />
        </div>
      )}
      <div className='mt-6'>
        {isPuntored ? (
          puntoredPartnersUrls.length > 0 && (
            <div className='paycash-partners-grid'>
              {puntoredPartnersUrls.map((url, index) => (
                <img key={index} src={url} alt={`Partner ${index + 1}`} />
              ))}
            </div>
          )
        ) : (
          partnersUrls.length > 0 && (
            <div className='paycash-partners-grid'>
              {partnersUrls.map((url, index) => (
                <img key={index} src={url} alt={`Partner ${index + 1}`} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

PaymentReference.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  payment: PropTypes.object,
  orders: PropTypes.array,
  amount: PropTypes.number.isRequired,
  coverage: PropTypes.number,
  coverageId: PropTypes.string,
  isMultiple: PropTypes.bool,
  provider: PropTypes.string,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  onDownloadSectionNote: PropTypes.func,
};

export default PaymentReference;
